import { useEffect, useMemo, useRef, useState } from "react";
import api, { openPdf } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2, LayoutGrid, ListChecks, MapPin, Save, Download, Printer, Image as ImageIcon, Upload, Settings2, Check, QrCode } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../context/ConfirmContext";

const GRID_COLS = 30;
const GRID_ROWS = 20;
const CELL = 26; // pixel per grid cell

const CATEGORY_LABEL = {
  raw: "Rohstoff", finished: "Fertigware",
  packaging: "Verpackung", mixed: "Gemischt", other: "Sonstiges",
};

const CATEGORY_COLOR = {
  raw: "#f59e0b", finished: "#10b981",
  packaging: "#3b82f6", mixed: "#a855f7", other: "#6b7280",
};

/** Heatmap-Farbe für Füllgrad in %.  0 → grün, 50 → gelb, 100 → rot. */
function heatmapColor(pct) {
  if (pct == null || Number.isNaN(pct)) return "#e5e7eb";
  const p = Math.max(0, Math.min(100, Number(pct)));
  const lerp = (a, b, t) => Math.round(a + (b - a) * t);
  if (p <= 50) {
    const t = p / 50;
    // #10b981 (grün) → #f59e0b (gelb)
    return `rgb(${lerp(16, 245, t)}, ${lerp(185, 158, t)}, ${lerp(129, 11, t)})`;
  }
  const t = (p - 50) / 50;
  // #f59e0b → #ef4444 (rot)
  return `rgb(${lerp(245, 239, t)}, ${lerp(158, 68, t)}, ${lerp(11, 68, t)})`;
}

const TABS = [
  { key: "list", label: "Regal-Liste", icon: ListChecks },
  { key: "layout", label: "Layout (Anordnung)", icon: LayoutGrid },
  { key: "zones", label: "Zonen", icon: MapPin },
];

export default function ShelvesManage() {
  const [tab, setTab] = useState("list");
  const [shelves, setShelves] = useState([]);
  const [zones, setZones] = useState([]);
  const [shelfDlg, setShelfDlg] = useState(null);
  const [zoneDlg, setZoneDlg] = useState(null);
  const [rowDlg, setRowDlg] = useState(false);
  const [layoutCfgOpen, setLayoutCfgOpen] = useState(false);
  const [qrLabelsOpen, setQrLabelsOpen] = useState(false);
  const [bulkSelected, setBulkSelected] = useState([]);

  const load = async () => {
    const [s, z] = await Promise.all([
      api.get("/shelves"), api.get("/shelves/zones"),
    ]);
    setShelves(s.data); setZones(z.data);
  };
  useEffect(() => { load(); }, []);

  const importFromBatches = async () => {
    if (!window.confirm("Regale aus vorhandenen Chargen-Standorten importieren?")) return;
    try {
      const { data } = await api.post("/shelves/import-from-batches");
      toast.success(`${data.count} Regal(e) angelegt${data.count ? ": " + data.created.join(", ") : ""}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  return (
    <div data-testid="page-shelves-manage">
      <PageHeader
        eyebrow="Lager"
        title="Regal-Verwaltung"
        description="Regale als DB-Entität pflegen, Zonen definieren und Regale visuell im Grid anordnen."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setLayoutCfgOpen(true)}
                    className="rounded-full" data-testid="btn-layout-configurator">
              <Settings2 size={14} className="mr-1" /> Layout-Konfigurator
            </Button>
            <Button variant="outline"
                    onClick={() => setQrLabelsOpen(true)}
                    className="rounded-full" data-testid="btn-open-qr-labels-dialog">
              <QrCode size={14} className="mr-1" /> QR-Etiketten drucken
            </Button>
            <Button variant="outline"
                    onClick={() => openPdf(`/shelves/labels.pdf`).catch((err) => toast.error(err.response?.data?.detail || "Etiketten-Fehler"))}
                    className="rounded-full" data-testid="btn-all-shelf-labels">
              <Printer size={14} className="mr-1" /> Alle Etiketten ({shelves.length})
            </Button>
            <Button variant="outline" onClick={() => openPdf(`/shelves/tour.pdf`).catch(() => toast.error("Rundgang-Fehler"))}
                    className="rounded-full" data-testid="btn-tour-all">
              <Printer size={14} className="mr-1" /> Rundgang (alle)
            </Button>
            <Button variant="outline" onClick={importFromBatches}
                    className="rounded-full" data-testid="btn-import-shelves">
              <Download size={14} className="mr-1" /> Aus Chargen importieren
            </Button>
          </div>
        }
      />
      {layoutCfgOpen && (
        <LayoutConfiguratorDialog onClose={() => setLayoutCfgOpen(false)}
                                   onSaved={() => { setLayoutCfgOpen(false); load(); }} />
      )}
      {qrLabelsOpen && (
        <QrLabelsDialog shelves={shelves} initialSelected={bulkSelected}
                         onClose={() => setQrLabelsOpen(false)} />
      )}

      <div className="flex items-center gap-2 border-b border-[var(--border)] mb-6" data-testid="shelves-tabs">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
                  data-testid={`tab-${key}`}
                  className={`inline-flex items-center gap-2 px-4 py-2 border-b-2 text-sm font-semibold transition-colors ${
                    tab === key
                      ? "border-[var(--pop-pink)] text-[var(--pop-black)]"
                      : "border-transparent text-[var(--text-muted)] hover:text-[var(--pop-black)]"
                  }`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === "list" && (
        <ShelfList shelves={shelves} zones={zones}
                    onNew={() => setShelfDlg({ mode: "create" })}
                    onNewRow={() => setRowDlg(true)}
                    onEdit={(s) => setShelfDlg({ mode: "edit", ...s })}
                    reload={load} />
      )}
      {tab === "layout" && (
        <LayoutEditor shelves={shelves} zones={zones} reload={load} />
      )}
      {tab === "zones" && (
        <ZoneList zones={zones} shelves={shelves}
                    onNew={() => setZoneDlg({ mode: "create" })}
                    onEdit={(z) => setZoneDlg({ mode: "edit", ...z })}
                    reload={load} />
      )}

      {shelfDlg && (
        <ShelfDialog initial={shelfDlg} zones={zones}
                       onClose={() => setShelfDlg(null)}
                       onSaved={() => { setShelfDlg(null); load(); }} />
      )}
      {rowDlg && (
        <RowTemplateDialog zones={zones}
                            onClose={() => setRowDlg(false)}
                            onSaved={() => { setRowDlg(false); load(); }} />
      )}
      {zoneDlg && (
        <ZoneDialog initial={zoneDlg}
                      onClose={() => setZoneDlg(null)}
                      onSaved={() => { setZoneDlg(null); load(); }} />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Regal-Liste                                                                */
/* -------------------------------------------------------------------------- */
function ShelfList({ shelves, zones, onNew, onNewRow, onEdit, reload }) {
  const zMap = useMemo(() => Object.fromEntries(zones.map((z) => [z.id, z])), [zones]);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkZone, setBulkZone] = useState("none");
  const [bulkCat, setBulkCat] = useState("");
  const confirm = useConfirm();

  useEffect(() => {
    // Prune selection when list changes
    setSelected((prev) => {
      const ids = new Set(shelves.map((s) => s.id));
      const next = new Set();
      for (const id of prev) if (ids.has(id)) next.add(id);
      return next;
    });
  }, [shelves]);

  const toggle = (id) =>
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () =>
    setSelected((prev) => prev.size === shelves.length ? new Set() : new Set(shelves.map((s) => s.id)));

  const bulkDelete = async () => {
    if (!selected.size) return;
    const ok = await confirm({
      title: `${selected.size} Regale löschen?`,
      description: "Die markierten Regale werden endgültig entfernt. Chargen an diesen Standorten verlieren ihre Regal-Zuordnung.",
      confirmLabel: "Endgültig löschen",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      const { data } = await api.post("/shelves/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${data.deleted} Regale gelöscht`);
      setSelected(new Set()); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const bulkUpdate = async () => {
    if (!selected.size) return toast.error("Keine Regale markiert");
    const patch = {};
    if (bulkZone !== "none") patch.zone_id = bulkZone === "clear" ? null : bulkZone;
    if (bulkCat) patch.category = bulkCat;
    if (!Object.keys(patch).length) return toast.error("Kein Feld zum Ändern gewählt");
    try {
      const { data } = await api.post("/shelves/bulk-update", { ids: Array.from(selected), patch });
      toast.success(`${data.updated} Regale aktualisiert`);
      setSelected(new Set()); setBulkZone("none"); setBulkCat(""); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const del = async (s) => {
    if (!window.confirm(`Regal ${s.code} löschen?`)) return;
    try { await api.delete(`/shelves/${s.id}`); toast.success("Gelöscht"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };
  return (
    <Card className="p-0 overflow-hidden rounded-2xl">
      <div className="p-3 border-b flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap text-sm" data-testid="bulk-bar">
          {selected.size > 0 ? (
            <>
              <span className="pop-pill text-[10px] font-bold bg-[var(--pop-pink)]/20 text-[var(--pop-pink)]"
                    data-testid="bulk-selected-count">
                {selected.size} markiert
              </span>
              <Select value={bulkZone} onValueChange={setBulkZone}>
                <SelectTrigger className="h-8 w-40" data-testid="bulk-zone-select">
                  <SelectValue placeholder="Zone ändern…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Zone (unverändert) —</SelectItem>
                  <SelectItem value="clear">— Zone entfernen —</SelectItem>
                  {zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={bulkCat} onValueChange={setBulkCat}>
                <SelectTrigger className="h-8 w-44" data-testid="bulk-cat-select">
                  <SelectValue placeholder="Kategorie…" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={bulkUpdate}
                      data-testid="btn-bulk-apply" className="rounded-full">
                <Check size={12} className="mr-1" /> Anwenden
              </Button>
              <Button size="sm" variant="outline" onClick={bulkDelete}
                      data-testid="btn-bulk-delete"
                      className="rounded-full text-red-700 border-red-200 hover:bg-red-50">
                <Trash2 size={12} className="mr-1" /> Löschen
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}
                      data-testid="btn-bulk-clear" className="rounded-full">
                Aufheben
              </Button>
            </>
          ) : (
            <span className="text-xs text-[var(--text-muted)]">Markiere Regale für Bulk-Aktionen.</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-full"
                  data-testid="btn-new-row" onClick={onNewRow}>
            <Plus size={14} className="mr-1" /> Reihe anlegen
          </Button>
          <Button className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold"
                  data-testid="btn-new-shelf" onClick={onNew}>
            <Plus size={14} className="mr-1" /> Neues Regal
          </Button>
        </div>
      </div>
      <Table className="table-zebra">
        <TableHeader>
          <TableRow>
            <TableHead className="overline w-8">
              <input type="checkbox" data-testid="chk-all-shelves"
                     checked={selected.size === shelves.length && shelves.length > 0}
                     onChange={toggleAll} />
            </TableHead>
            <TableHead className="overline">Code</TableHead>
            <TableHead className="overline">Name</TableHead>
            <TableHead className="overline">Zone</TableHead>
            <TableHead className="overline">Kategorie</TableHead>
            <TableHead className="overline text-right">Kapazität</TableHead>
            <TableHead className="overline text-right">Bestand</TableHead>
            <TableHead className="overline text-right">Füllgrad</TableHead>
            <TableHead className="overline text-right">Aktion</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {shelves.map((s) => (
            <TableRow key={s.id} data-testid={`shelf-row-${s.code}`}
                      className={selected.has(s.id) ? "bg-[var(--pop-pink)]/5" : ""}>
              <TableCell>
                <input type="checkbox" data-testid={`chk-shelf-${s.code}`}
                       checked={selected.has(s.id)}
                       onChange={() => toggle(s.id)} />
              </TableCell>
              <TableCell className="font-mono font-bold">{s.code}</TableCell>
              <TableCell>{s.name || "—"}</TableCell>
              <TableCell>
                {zMap[s.zone_id]
                  ? <span className="pop-pill text-[10px] font-bold" style={{ background: (zMap[s.zone_id].color || "#eb75b5") + "20", color: zMap[s.zone_id].color || "#eb75b5" }}>{zMap[s.zone_id].name}</span>
                  : <span className="text-xs text-[var(--text-muted)]">—</span>}
              </TableCell>
              <TableCell>
                <span className="pop-pill text-[10px] font-bold"
                      style={{ background: CATEGORY_COLOR[s.category] + "20", color: CATEGORY_COLOR[s.category] }}>
                  {CATEGORY_LABEL[s.category] || s.category}
                </span>
              </TableCell>
              <TableCell className="text-right font-mono">{s.capacity || "—"}</TableCell>
              <TableCell className="text-right font-mono">
                {Number(s.current_units || 0).toLocaleString("de-DE")}
                {s.current_batches ? <span className="text-[10px] text-[var(--text-muted)] ml-1">({s.current_batches} Ch.)</span> : null}
              </TableCell>
              <TableCell className="text-right">
                {s.fill_pct != null ? (
                  <span className={`pop-pill text-[10px] font-bold ${s.fill_pct >= 80 ? "bg-red-100 text-red-700" : s.fill_pct >= 50 ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-700"}`}>
                    {s.fill_pct}%
                  </span>
                ) : <span className="text-xs text-[var(--text-muted)]">—</span>}
              </TableCell>
              <TableCell className="text-right whitespace-nowrap">
                <button onClick={() => onEdit(s)} className="text-xs font-semibold hover:underline mr-3"
                        data-testid={`btn-edit-shelf-${s.code}`}>Bearbeiten</button>
                <button onClick={() => del(s)} className="text-xs font-semibold text-red-700 hover:underline"
                        data-testid={`btn-delete-shelf-${s.code}`}>
                  <Trash2 size={12} className="inline mr-1" />
                </button>
              </TableCell>
            </TableRow>
          ))}
          {shelves.length === 0 && (
            <TableRow><TableCell colSpan={9} className="text-center py-8 text-[var(--text-muted)]">
              Noch keine Regale. Klick &bdquo;Aus Chargen importieren&ldquo; oder &bdquo;Neues Regal&ldquo;.
            </TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Layout Editor (Drag & Drop im Grid)                                        */
/* -------------------------------------------------------------------------- */
function LayoutEditor({ shelves, zones, reload }) {
  const [items, setItems] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [drag, setDrag] = useState(null); // {id, startX, startY, offX, offY, mode: 'move'|'resize'}
  const [saving, setSaving] = useState(false);
  const [selectedZoneId, setSelectedZoneId] = useState("all");
  const [floorplanOpacity, setFloorplanOpacity] = useState(0.5);
  const [uploading, setUploading] = useState(false);
  const [heatmap, setHeatmap] = useState(false);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const zMap = useMemo(() => Object.fromEntries(zones.map((z) => [z.id, z])), [zones]);
  const activeZone = selectedZoneId !== "all" ? zMap[selectedZoneId] : null;

  useEffect(() => {
    setItems(shelves.map((s) => ({ ...s })));
    setDirty(false);
  }, [shelves]);

  const onMouseDown = (e, s, mode = "move") => {
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    setDrag({
      id: s.id, mode,
      startX: e.clientX - rect.left,
      startY: e.clientY - rect.top,
      origX: s.grid_x, origY: s.grid_y,
      origW: s.grid_w, origH: s.grid_h,
    });
  };

  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const rect = canvasRef.current.getBoundingClientRect();
      const dx = Math.round((e.clientX - rect.left - drag.startX) / CELL);
      const dy = Math.round((e.clientY - rect.top - drag.startY) / CELL);
      setItems((prev) => prev.map((it) => {
        if (it.id !== drag.id) return it;
        if (drag.mode === "move") {
          return {
            ...it,
            grid_x: Math.max(0, Math.min(GRID_COLS - it.grid_w, drag.origX + dx)),
            grid_y: Math.max(0, Math.min(GRID_ROWS - it.grid_h, drag.origY + dy)),
          };
        } else {
          return {
            ...it,
            grid_w: Math.max(1, Math.min(GRID_COLS - it.grid_x, drag.origW + dx)),
            grid_h: Math.max(1, Math.min(GRID_ROWS - it.grid_y, drag.origH + dy)),
          };
        }
      }));
      setDirty(true);
    };
    const onUp = () => setDrag(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [drag]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = items.map((it) => ({
        id: it.id, grid_x: it.grid_x, grid_y: it.grid_y,
        grid_w: it.grid_w, grid_h: it.grid_h,
      }));
      const { data } = await api.post("/shelves/layout/bulk-position", payload);
      toast.success(`${data.updated} Regale gespeichert`);
      setDirty(false); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setSaving(false); }
  };

  const uploadFloorplan = async (file) => {
    if (!activeZone) return toast.error("Bitte erst eine Zone auswählen");
    if (file.size > 5 * 1024 * 1024) return toast.error("Bild zu groß (max 5 MB)");
    setUploading(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      await api.patch(`/shelves/zones/${activeZone.id}`, {
        name: activeZone.name, color: activeZone.color,
        sort_order: activeZone.sort_order || 0,
        notes: activeZone.notes || null,
        floorplan_data: dataUrl,
      });
      toast.success("Grundriss hochgeladen");
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload fehlgeschlagen"); }
    finally { setUploading(false); }
  };

  const removeFloorplan = async () => {
    if (!activeZone) return;
    if (!window.confirm("Grundriss entfernen?")) return;
    await api.patch(`/shelves/zones/${activeZone.id}`, {
      name: activeZone.name, color: activeZone.color,
      sort_order: activeZone.sort_order || 0,
      notes: activeZone.notes || null,
      floorplan_data: null,
    });
    toast.success("Grundriss entfernt");
    reload();
  };

  const visibleShelves = selectedZoneId === "all"
    ? items
    : items.filter((s) => s.zone_id === selectedZoneId);

  return (
    <Card className="p-4 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={selectedZoneId} onValueChange={setSelectedZoneId}>
            <SelectTrigger className="w-52" data-testid="select-layout-zone">
              <SelectValue placeholder="Alle Zonen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Zonen</SelectItem>
              {zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {activeZone && (
            <>
              <input ref={fileRef} type="file" accept="image/*" className="hidden"
                     data-testid="input-floorplan-file"
                     onChange={(e) => e.target.files?.[0] && uploadFloorplan(e.target.files[0])} />
              <Button size="sm" variant="outline" className="rounded-full"
                      onClick={() => fileRef.current?.click()} disabled={uploading}
                      data-testid="btn-upload-floorplan">
                <Upload size={14} className="mr-1" />
                {uploading ? "Lade…" : activeZone.floorplan_data ? "Grundriss ändern" : "Grundriss hochladen"}
              </Button>
              {activeZone.floorplan_data && (
                <>
                  <div className="flex items-center gap-2 text-xs">
                    <ImageIcon size={12} className="text-[var(--text-muted)]" />
                    <span className="text-[var(--text-muted)]">Deckkraft</span>
                    <input type="range" min="0.1" max="1" step="0.1"
                           value={floorplanOpacity}
                           onChange={(e) => setFloorplanOpacity(Number(e.target.value))}
                           className="w-24" data-testid="input-floorplan-opacity" />
                    <span className="font-mono">{Math.round(floorplanOpacity * 100)}%</span>
                  </div>
                  <Button size="sm" variant="ghost" className="rounded-full text-xs text-red-700"
                          onClick={removeFloorplan} data-testid="btn-remove-floorplan">
                    Entfernen
                  </Button>
                </>
              )}
            </>
          )}
        </div>
        <Button onClick={save} disabled={!dirty || saving}
                data-testid="btn-save-layout"
                className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
          <Save size={14} className="mr-1" /> {saving ? "Speichere…" : dirty ? "Layout speichern" : "Gespeichert"}
        </Button>
      </div>
      <div className="flex items-center gap-3 mb-3 text-xs flex-wrap">
        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="chk-heatmap-label">
          <input type="checkbox" checked={heatmap}
                 data-testid="chk-heatmap"
                 onChange={(e) => setHeatmap(e.target.checked)} />
          <b>Kapazitäts-Heatmap</b>
          <span className="text-[var(--text-muted)]">Regale nach Füllgrad einfärben (grün→gelb→rot)</span>
        </label>
        {heatmap && (
          <div className="inline-flex items-center gap-2" data-testid="heatmap-scale">
            <span className="text-[var(--text-muted)]">Skala:</span>
            <span className="inline-block w-4 h-3 rounded" style={{ background: heatmapColor(0) }} /><span>0%</span>
            <span className="inline-block w-4 h-3 rounded" style={{ background: heatmapColor(25) }} /><span>25%</span>
            <span className="inline-block w-4 h-3 rounded" style={{ background: heatmapColor(50) }} /><span>50%</span>
            <span className="inline-block w-4 h-3 rounded" style={{ background: heatmapColor(75) }} /><span>75%</span>
            <span className="inline-block w-4 h-3 rounded" style={{ background: heatmapColor(100) }} /><span>100%</span>
          </div>
        )}
      </div>
      <div className="text-xs text-[var(--text-muted)] mb-2">
        Regale ziehen: <b>drag &amp; drop</b>. Größe ändern: unten-rechts-Ecke ziehen. Farbe = Zone.
        {activeZone && !activeZone.floorplan_data && " · Lade oben ein Hallenplan-Bild hoch, um Regale exakt zu positionieren."}
      </div>
      <div className="border-2 rounded-xl bg-[var(--pop-cream)]/30 overflow-auto p-3">
        <div
          ref={canvasRef}
          data-testid="layout-canvas"
          style={{
            position: "relative",
            width: GRID_COLS * CELL,
            height: GRID_ROWS * CELL,
            backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.06) 1px, transparent 1px)`,
            backgroundSize: `${CELL}px ${CELL}px`,
            userSelect: "none",
          }}
        >
          {activeZone?.floorplan_data && (
            <img src={activeZone.floorplan_data} alt="Grundriss"
                 data-testid="floorplan-bg"
                 style={{
                   position: "absolute", left: 0, top: 0,
                   width: "100%", height: "100%",
                   objectFit: "contain",
                   opacity: floorplanOpacity,
                   pointerEvents: "none",
                 }} />
          )}
          {visibleShelves.map((s) => {
            const zoneColor = zMap[s.zone_id]?.color || CATEGORY_COLOR[s.category] || "#6b7280";
            const baseColor = heatmap ? heatmapColor(s.fill_pct) : zoneColor;
            // Front/Back detection: code like "A01-.." or "B01-.." (also legacy "R1F-.." / "R1B-..")
            const m = /^([AB])\d+-/i.exec(s.code || "") || /^R\d+([FB])-/i.exec(s.code || "");
            const side = m ? m[1].toUpperCase() : null;
            const isBack = side === "B";
            const fillBg = heatmap
              ? baseColor
              : (isBack ? baseColor + "66" : baseColor + "e6");
            const borderStyle = isBack ? "dashed" : "solid";
            return (
              <div key={s.id}
                   data-testid={`layout-shelf-${s.code}`}
                   onMouseDown={(e) => onMouseDown(e, s, "move")}
                   title={`${s.code}${side ? " · " + (isBack ? "Rückseite" : "Vorderseite") : ""}${s.fill_pct != null ? " · " + s.fill_pct + "%" : ""}`}
                   style={{
                     position: "absolute",
                     left: s.grid_x * CELL,
                     top: s.grid_y * CELL,
                     width: s.grid_w * CELL,
                     height: s.grid_h * CELL,
                     background: fillBg,
                     border: `2px ${borderStyle} ${heatmap ? baseColor : baseColor}`,
                     borderRadius: 6,
                     cursor: "grab",
                     color: isBack && !heatmap ? "#1f2937" : "#fff",
                     fontFamily: "monospace",
                     fontWeight: 700,
                     display: "flex",
                     alignItems: "center",
                     justifyContent: "center",
                     boxShadow: "0 2px 6px rgba(0,0,0,.15)",
                     fontSize: 10,
                     overflow: "hidden",
                   }}>
                <div style={{ textAlign: "center", pointerEvents: "none", padding: 2 }}>
                  <div style={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden", fontSize: 9 }}>{s.code}</div>
                  {s.fill_pct != null && <div style={{ fontSize: 10, opacity: .95, fontWeight: 800 }}>{s.fill_pct}%</div>}
                </div>
                <div onMouseDown={(e) => { e.stopPropagation(); onMouseDown(e, s, "resize"); }}
                     style={{
                       position: "absolute", right: 0, bottom: 0,
                       width: 10, height: 10,
                       background: "rgba(255,255,255,.7)", cursor: "nwse-resize",
                       borderTopLeftRadius: 4,
                     }} />
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-3 flex items-center gap-3 flex-wrap text-xs text-[var(--text-muted)]">
        <span>Legende:</span>
        {zones.map((z) => (
          <span key={z.id} className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded" style={{ background: z.color || "#eb75b5" }} /> {z.name}
          </span>
        ))}
        {zones.length === 0 && <span>Keine Zonen — Farbe folgt Kategorie.</span>}
        <span className="mx-2">·</span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded" style={{ background: "#eb75b5e6", border: "2px solid #eb75b5" }} /> Vorderseite (F)
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded" style={{ background: "#eb75b566", border: "2px dashed #eb75b5" }} /> Rückseite (B)
        </span>
      </div>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Zonen-Liste                                                                */
/* -------------------------------------------------------------------------- */
function ZoneList({ zones, shelves, onNew, onEdit, reload }) {
  const counts = useMemo(() => {
    const m = {}; for (const s of shelves) m[s.zone_id] = (m[s.zone_id] || 0) + 1;
    return m;
  }, [shelves]);
  const del = async (z) => {
    if (!window.confirm(`Zone "${z.name}" löschen?`)) return;
    try { await api.delete(`/shelves/zones/${z.id}`); toast.success("Gelöscht"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };
  return (
    <Card className="p-0 overflow-hidden rounded-2xl">
      <div className="p-3 border-b flex items-center justify-end">
        <Button className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold"
                data-testid="btn-new-zone" onClick={onNew}>
          <Plus size={14} className="mr-1" /> Neue Zone
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="overline">Name</TableHead>
            <TableHead className="overline">Farbe</TableHead>
            <TableHead className="overline text-right">Regale</TableHead>
            <TableHead className="overline text-right">Aktion</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {zones.map((z) => (
            <TableRow key={z.id} data-testid={`zone-row-${z.id}`}>
              <TableCell className="font-medium">{z.name}</TableCell>
              <TableCell>
                <span className="inline-flex items-center gap-2">
                  <span className="inline-block w-4 h-4 rounded" style={{ background: z.color || "#eb75b5" }} />
                  <span className="font-mono text-xs">{z.color || "—"}</span>
                </span>
              </TableCell>
              <TableCell className="text-right font-mono">{counts[z.id] || 0}</TableCell>
              <TableCell className="text-right whitespace-nowrap">
                <button onClick={() => openPdf(`/shelves/tour.pdf?zone_id=${z.id}`).catch((err) => toast.error(err.response?.data?.detail || "Rundgang-Fehler"))}
                        className="text-xs font-semibold text-[var(--pop-black)] hover:underline mr-3 inline-flex items-center gap-1"
                        data-testid={`btn-tour-zone-${z.id}`}
                        disabled={!counts[z.id]}
                        title={!counts[z.id] ? "Keine Regale in dieser Zone" : ""}>
                  <Printer size={12} /> Rundgang
                </button>
                <button onClick={() => openPdf(`/shelves/zones/${z.id}/labels.pdf`).catch((err) => toast.error(err.response?.data?.detail || "Etiketten-Fehler"))}
                        className="text-xs font-semibold text-[var(--pop-pink)] hover:underline mr-3 inline-flex items-center gap-1"
                        data-testid={`btn-print-zone-labels-${z.id}`}
                        disabled={!counts[z.id]}
                        title={!counts[z.id] ? "Keine Regale in dieser Zone" : ""}>
                  <Printer size={12} /> Etiketten
                </button>
                <button onClick={() => onEdit(z)} className="text-xs font-semibold hover:underline mr-3"
                        data-testid={`btn-edit-zone-${z.id}`}>Bearbeiten</button>
                <button onClick={() => del(z)} className="text-xs font-semibold text-red-700 hover:underline"
                        data-testid={`btn-delete-zone-${z.id}`}>
                  <Trash2 size={12} className="inline mr-1" />
                </button>
              </TableCell>
            </TableRow>
          ))}
          {zones.length === 0 && (
            <TableRow><TableCell colSpan={4} className="text-center py-8 text-[var(--text-muted)]">
              Keine Zonen. Beispiel: &bdquo;Halle A&ldquo;, &bdquo;K&uuml;hlhaus&ldquo;, &bdquo;Au&szlig;enlager&ldquo;.
            </TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Dialoge                                                                    */
/* -------------------------------------------------------------------------- */
function ShelfDialog({ initial, zones, onClose, onSaved }) {
  const isEdit = initial.mode === "edit";
  const [f, setF] = useState({
    code: initial.code || "",
    name: initial.name || "",
    zone_id: initial.zone_id || "",
    category: initial.category || "mixed",
    capacity: initial.capacity || "",
    grid_x: initial.grid_x ?? 0,
    grid_y: initial.grid_y ?? 0,
    grid_w: initial.grid_w ?? 2,
    grid_h: initial.grid_h ?? 1,
    notes: initial.notes || "",
    active: initial.active !== false,
  });
  const save = async () => {
    try {
      const payload = {
        ...f,
        zone_id: f.zone_id || null,
        capacity: f.capacity === "" ? null : Number(f.capacity),
        grid_x: Number(f.grid_x), grid_y: Number(f.grid_y),
        grid_w: Number(f.grid_w), grid_h: Number(f.grid_h),
      };
      if (isEdit) await api.patch(`/shelves/${initial.id}`, payload);
      else await api.post("/shelves", payload);
      toast.success("Gespeichert"); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="dialog-shelf">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {isEdit ? `Regal ${initial.code}` : "Neues Regal"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Code {!isEdit && <span className="text-[10px] text-[var(--text-muted)]">(leer = Auto)</span>}</Label>
            <Input value={f.code} data-testid="input-shelf-code"
                   placeholder="z.B. A-01-03"
                   onChange={(e) => setF({ ...f, code: e.target.value })} />
          </div>
          <div>
            <Label>Name (Bezeichnung)</Label>
            <Input value={f.name} data-testid="input-shelf-name"
                   onChange={(e) => setF({ ...f, name: e.target.value })} />
          </div>
          <div>
            <Label>Zone</Label>
            <Select value={f.zone_id || "none"} onValueChange={(v) => setF({ ...f, zone_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="select-shelf-zone"><SelectValue placeholder="Keine…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Keine Zone —</SelectItem>
                {zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Kategorie</Label>
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger data-testid="select-shelf-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Kapazität (Einheiten)</Label>
            <Input type="number" min="0" value={f.capacity}
                   data-testid="input-shelf-capacity"
                   onChange={(e) => setF({ ...f, capacity: e.target.value })} />
          </div>
          <div />
          <div>
            <Label>Grid X</Label>
            <Input type="number" min="0" max={GRID_COLS - 1} value={f.grid_x}
                   onChange={(e) => setF({ ...f, grid_x: e.target.value })} />
          </div>
          <div>
            <Label>Grid Y</Label>
            <Input type="number" min="0" max={GRID_ROWS - 1} value={f.grid_y}
                   onChange={(e) => setF({ ...f, grid_y: e.target.value })} />
          </div>
          <div>
            <Label>Breite</Label>
            <Input type="number" min="1" max={GRID_COLS} value={f.grid_w}
                   onChange={(e) => setF({ ...f, grid_w: e.target.value })} />
          </div>
          <div>
            <Label>Höhe</Label>
            <Input type="number" min="1" max={GRID_ROWS} value={f.grid_h}
                   onChange={(e) => setF({ ...f, grid_h: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Label>Notizen</Label>
            <Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Abbrechen</Button>
          <Button onClick={save} data-testid="btn-save-shelf"
                  className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RowTemplateDialog({ zones, onClose, onSaved }) {
  const [f, setF] = useState({
    prefix: "A", start: 1, end: 20, pad: 2, separator: "-",
    zone_id: "", category: "mixed", capacity: "",
    grid_x: 0, grid_y: 0, grid_w: 2, grid_h: 1,
    direction: "horizontal", gap: 0,
  });
  const preview = useMemo(() => {
    const n = Math.max(0, Number(f.end) - Number(f.start) + 1);
    if (n === 0) return "";
    const s = [f.start, Number(f.start) + 1, f.end].map((x) => `${f.prefix}${f.separator}${String(x).padStart(Number(f.pad), "0")}`);
    return n <= 3 ? s.slice(0, n).join(", ") : `${s[0]}, ${s[1]}, …, ${s[2]}  (${n} Regale)`;
  }, [f]);
  const save = async () => {
    try {
      const { data } = await api.post("/shelves/bulk-create", {
        prefix: f.prefix, separator: f.separator,
        start: Number(f.start), end: Number(f.end), pad: Number(f.pad),
        zone_id: f.zone_id || null, category: f.category,
        capacity: f.capacity === "" ? null : Number(f.capacity),
        grid_x: Number(f.grid_x), grid_y: Number(f.grid_y),
        grid_w: Number(f.grid_w), grid_h: Number(f.grid_h),
        direction: f.direction, gap: Number(f.gap),
      });
      toast.success(`${data.count} Regale angelegt${data.skipped.length ? ` · ${data.skipped.length} schon vorhanden` : ""}`);
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="dialog-row-template">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Regal-Reihe anlegen</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-[var(--text-muted)] -mt-2 mb-2">
          Beispiel: Pr&auml;fix &bdquo;A&ldquo;, Start 1, Ende 20, Padding 2 &rarr; <b>A-01, A-02, &hellip;, A-20</b>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Präfix</Label>
            <Input value={f.prefix} data-testid="input-row-prefix"
                   onChange={(e) => setF({ ...f, prefix: e.target.value })} />
          </div>
          <div>
            <Label>Trennzeichen</Label>
            <Input value={f.separator} maxLength={2}
                   onChange={(e) => setF({ ...f, separator: e.target.value })} />
          </div>
          <div>
            <Label>Padding (Ziffern)</Label>
            <Input type="number" min="0" max="5" value={f.pad}
                   onChange={(e) => setF({ ...f, pad: e.target.value })} />
          </div>
          <div>
            <Label>Start</Label>
            <Input type="number" value={f.start} data-testid="input-row-start"
                   onChange={(e) => setF({ ...f, start: e.target.value })} />
          </div>
          <div>
            <Label>Ende</Label>
            <Input type="number" value={f.end} data-testid="input-row-end"
                   onChange={(e) => setF({ ...f, end: e.target.value })} />
          </div>
          <div>
            <Label>Kapazität pro Regal</Label>
            <Input type="number" min="0" value={f.capacity} data-testid="input-row-capacity"
                   placeholder="—" onChange={(e) => setF({ ...f, capacity: e.target.value })} />
          </div>
          <div>
            <Label>Zone</Label>
            <Select value={f.zone_id || "none"} onValueChange={(v) => setF({ ...f, zone_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="select-row-zone"><SelectValue placeholder="Keine…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Keine —</SelectItem>
                {zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Kategorie</Label>
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger data-testid="select-row-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Richtung</Label>
            <Select value={f.direction} onValueChange={(v) => setF({ ...f, direction: v })}>
              <SelectTrigger data-testid="select-row-direction"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="horizontal">Horizontal (Reihe →)</SelectItem>
                <SelectItem value="vertical">Vertikal (Spalte ↓)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Grid X (Start)</Label>
            <Input type="number" min="0" value={f.grid_x}
                   onChange={(e) => setF({ ...f, grid_x: e.target.value })} />
          </div>
          <div>
            <Label>Grid Y (Start)</Label>
            <Input type="number" min="0" value={f.grid_y}
                   onChange={(e) => setF({ ...f, grid_y: e.target.value })} />
          </div>
          <div>
            <Label>Abstand (Zellen)</Label>
            <Input type="number" min="0" value={f.gap}
                   onChange={(e) => setF({ ...f, gap: e.target.value })} />
          </div>
          <div>
            <Label>Breite je Regal</Label>
            <Input type="number" min="1" value={f.grid_w}
                   onChange={(e) => setF({ ...f, grid_w: e.target.value })} />
          </div>
          <div>
            <Label>Höhe je Regal</Label>
            <Input type="number" min="1" value={f.grid_h}
                   onChange={(e) => setF({ ...f, grid_h: e.target.value })} />
          </div>
        </div>
        {preview && (
          <div className="mt-3 p-3 rounded-xl bg-[var(--pop-cream)]/40 text-sm font-mono" data-testid="row-preview">
            <span className="text-[var(--text-muted)]">Vorschau: </span>{preview}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Abbrechen</Button>
          <Button onClick={save} data-testid="btn-save-row"
                  className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
            Reihe anlegen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function LayoutConfiguratorDialog({ onClose, onSaved }) {
  const [f, setF] = useState({
    racks: 6, levels: 4, pick_level: 3,
    positions_per_level: 3, single_capacity: 320, both_sides: true,
  });
  const [busy, setBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const confirm = useConfirm();

  const runImageLayout = async () => {
    const ok = await confirm({
      title: "Bild-Layout anwenden?",
      description: "Alle vorhandenen Regale werden gelöscht und durch das POPCRN-Foto-Layout ersetzt: 6 Racks (A01–A06) vorne + 6 Racks (B01–B06) hinten, 4 Ebenen, Pick-Ebene = Level 02 (320 Einzelplätze). Chargen behalten ihre location-Codes.",
      confirmLabel: "Alle löschen und neu anlegen",
      variant: "destructive",
    });
    if (!ok) return;
    setImgBusy(true);
    try {
      const { data } = await api.post(`/admin/setup/warehouse-layout-image?purge_existing=true&racks_per_side=6&pick_capacity=320`);
      toast.success(`${data.purged} gelöscht · ${data.created} neu angelegt (${data.total_shelves} gesamt)`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Anlegen");
    } finally { setImgBusy(false); }
  };

  const totalShelves = useMemo(() => {
    const sides = f.both_sides ? 2 : 1;
    const perSidePerRack = (f.levels - 1) * f.positions_per_level + 1; // pick level = 1 shelf
    return f.racks * sides * perSidePerRack;
  }, [f]);

  const totalCapacity = useMemo(() => {
    const sides = f.both_sides ? 2 : 1;
    const palettes = f.racks * sides * (f.levels - 1) * f.positions_per_level; // 1 unit each
    const singles = f.racks * sides * Number(f.single_capacity || 0);
    return { palettes, singles, total: palettes + singles };
  }, [f]);

  const run = async () => {
    setBusy(true);
    try {
      const q = new URLSearchParams({
        racks: f.racks, levels: f.levels, pick_level: f.pick_level,
        positions_per_level: f.positions_per_level,
        single_capacity: f.single_capacity,
        both_sides: f.both_sides,
      }).toString();
      const { data } = await api.post(`/admin/setup/warehouse-layout?${q}`);
      toast.success(`${data.created} Regale angelegt · ${data.skipped} übersprungen (${data.total_shelves} gesamt)`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Anlegen");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="dialog-layout-configurator">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Layout-Konfigurator</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-[var(--text-muted)] -mt-2 mb-2">
          Erzeugt ein Standard-Regal-Layout mit Palettenplätzen &amp; Kommissionierebene.
          Bereits vorhandene Codes werden übersprungen.
        </div>
        <div className="rounded-xl border-2 border-[var(--pop-pink)]/40 bg-[var(--pop-pink)]/5 p-3 mb-3 flex items-center justify-between gap-3"
             data-testid="cfg-image-layout">
          <div className="text-sm">
            <div className="font-bold mb-0.5">POPCRN Foto-Layout ⚡</div>
            <div className="text-xs text-[var(--text-muted)]">
              Ein-Klick: 6 Racks vorne (A01–A06) + 6 Racks hinten (B01–B06), 4 Ebenen, Pick-Ebene = L02.
              <b> Löscht alle bestehenden Regale.</b>
            </div>
          </div>
          <Button size="sm" onClick={runImageLayout} disabled={imgBusy}
                  data-testid="btn-run-image-layout"
                  className="rounded-full bg-[var(--pop-pink)] text-white hover:opacity-90 font-bold whitespace-nowrap">
            {imgBusy ? "Erstelle…" : "Foto-Layout anwenden"}
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Regale (pro Reihe)</Label>
            <Input type="number" min="1" value={f.racks} data-testid="input-cfg-racks"
                   onChange={(e) => setF({ ...f, racks: Number(e.target.value) })} />
          </div>
          <div>
            <Label>Ebenen (pro Seite)</Label>
            <Input type="number" min="1" value={f.levels} data-testid="input-cfg-levels"
                   onChange={(e) => setF({ ...f, levels: Number(e.target.value) })} />
          </div>
          <div>
            <Label>Kommissionier-Ebene</Label>
            <Input type="number" min="1" max={f.levels} value={f.pick_level} data-testid="input-cfg-pick-level"
                   onChange={(e) => setF({ ...f, pick_level: Number(e.target.value) })} />
          </div>
          <div>
            <Label>Palettenplätze / Ebene</Label>
            <Input type="number" min="1" value={f.positions_per_level} data-testid="input-cfg-pos"
                   onChange={(e) => setF({ ...f, positions_per_level: Number(e.target.value) })} />
          </div>
          <div>
            <Label>Kapazität Pick-Regal</Label>
            <Input type="number" min="0" value={f.single_capacity} data-testid="input-cfg-single-cap"
                   onChange={(e) => setF({ ...f, single_capacity: Number(e.target.value) })} />
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer" data-testid="chk-both-sides-label">
              <input type="checkbox" checked={f.both_sides} data-testid="chk-both-sides"
                     onChange={(e) => setF({ ...f, both_sides: e.target.checked })} />
              Vorder- + Rückseite
            </label>
          </div>
        </div>
        <div className="mt-3 p-3 rounded-xl bg-[var(--pop-cream)]/40 text-sm space-y-1" data-testid="cfg-preview">
          <div><span className="text-[var(--text-muted)]">Regale gesamt:</span> <b className="font-mono">{totalShelves}</b></div>
          <div><span className="text-[var(--text-muted)]">Kapazität:</span>{" "}
            <b className="font-mono">{totalCapacity.total.toLocaleString("de-DE")}</b>{" "}
            <span className="text-xs text-[var(--text-muted)]">
              ({totalCapacity.palettes} Paletten + {totalCapacity.singles.toLocaleString("de-DE")} Einzelplätze)
            </span>
          </div>
          <div className="text-xs text-[var(--text-muted)]">
            Codes (R-Schema): <span className="font-mono">R{1}{f.both_sides ? "F" : ""}-L1-P1</span>,{" "}
            <span className="font-mono">R{1}{f.both_sides ? "F" : ""}-L{f.pick_level}</span> (Pick),{" "}
            {f.both_sides && <span className="font-mono">R{1}B-L1-P1</span>}
            {" · "}Alternative: Foto-Layout oben nutzt <span className="font-mono">A01-01-01</span>/<span className="font-mono">B01-01-01</span>-Naming.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Abbrechen</Button>
          <Button onClick={run} disabled={busy} data-testid="btn-run-layout-config"
                  className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
            {busy ? "Erstelle…" : `Layout erzeugen (${totalShelves} Regale)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function QrLabelsDialog({ shelves, initialSelected = [], onClose }) {
  const [mode, setMode] = useState("all"); // all | rack | selected
  const [rack, setRack] = useState("A01");
  const [selected, setSelected] = useState(() => new Set(initialSelected));
  const [busy, setBusy] = useState(false);

  const racks = useMemo(() => {
    const set = new Set();
    for (const s of shelves) {
      const m = /^([AB]\d+)-/i.exec(s.code || "");
      if (m) set.add(m[1].toUpperCase());
    }
    return Array.from(set).sort();
  }, [shelves]);

  const filtered = useMemo(() => {
    if (mode === "all") return shelves;
    if (mode === "rack") return shelves.filter((s) => (s.code || "").toUpperCase().startsWith(rack + "-"));
    return shelves.filter((s) => selected.has(s.id));
  }, [mode, rack, selected, shelves]);

  const toggle = (id) =>
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setSelected(new Set(shelves.map((s) => s.id)));
  const clearAll = () => setSelected(new Set());

  const download = async () => {
    if (!filtered.length) return toast.error("Keine Regale ausgewählt");
    setBusy(true);
    try {
      let url = "/shelves/qr-labels.pdf";
      if (mode === "rack") url += `?prefix=${encodeURIComponent(rack)}`;
      else if (mode === "selected") url += `?ids=${Array.from(selected).join(",")}`;
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const label = mode === "all" ? "alle" : mode === "rack" ? rack : "auswahl";
      link.download = `regal-qr-etiketten-${label}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(`${filtered.length} Etiketten heruntergeladen`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="dialog-qr-labels">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">QR-Regal-Etiketten drucken</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-[var(--text-muted)] -mt-2 mb-3">
          Format <b>102×152mm Querformat</b>, 2 Etiketten (51×76mm) nebeneinander. Enthält großen Regal-Code, QR-Code
          und Klartext (Regal · Ebene · Fach). Wird als PDF heruntergeladen.
        </div>

        <div className="grid grid-cols-3 gap-2 mb-3" data-testid="qr-mode-select">
          <button onClick={() => setMode("all")} data-testid="qr-mode-all"
                  className={`rounded-xl border-2 p-3 text-left transition-colors ${
                    mode === "all" ? "border-[var(--pop-pink)] bg-[var(--pop-pink)]/10" : "border-[var(--border)] hover:bg-[var(--pop-cream)]/40"
                  }`}>
            <div className="text-2xl font-bold font-mono">{shelves.length}</div>
            <div className="text-xs font-semibold">Alle Regale</div>
          </button>
          <button onClick={() => setMode("rack")} data-testid="qr-mode-rack"
                  className={`rounded-xl border-2 p-3 text-left transition-colors ${
                    mode === "rack" ? "border-[var(--pop-pink)] bg-[var(--pop-pink)]/10" : "border-[var(--border)] hover:bg-[var(--pop-cream)]/40"
                  }`}>
            <div className="text-2xl font-bold font-mono">{racks.length}</div>
            <div className="text-xs font-semibold">Nach Regal</div>
          </button>
          <button onClick={() => setMode("selected")} data-testid="qr-mode-selected"
                  className={`rounded-xl border-2 p-3 text-left transition-colors ${
                    mode === "selected" ? "border-[var(--pop-pink)] bg-[var(--pop-pink)]/10" : "border-[var(--border)] hover:bg-[var(--pop-cream)]/40"
                  }`}>
            <div className="text-2xl font-bold font-mono">{selected.size}</div>
            <div className="text-xs font-semibold">Einzelauswahl</div>
          </button>
        </div>

        {mode === "rack" && (
          <div className="mb-3" data-testid="qr-rack-picker">
            <Label>Regal wählen</Label>
            <Select value={rack} onValueChange={setRack}>
              <SelectTrigger data-testid="qr-rack-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {racks.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        {mode === "selected" && (
          <div className="mb-3" data-testid="qr-selection-list">
            <div className="flex items-center justify-between mb-1">
              <Label>Regale auswählen ({selected.size})</Label>
              <div className="flex gap-2 text-xs">
                <button onClick={selectAll} data-testid="qr-select-all" className="text-[var(--pop-pink)] hover:underline">Alle</button>
                <button onClick={clearAll} data-testid="qr-clear-all" className="text-[var(--text-muted)] hover:underline">Keine</button>
              </div>
            </div>
            <div className="border rounded-xl max-h-64 overflow-y-auto p-2 grid grid-cols-3 gap-1 text-xs">
              {shelves.map((s) => {
                const on = selected.has(s.id);
                return (
                  <label key={s.id} data-testid={`qr-shelf-${s.code}`}
                         className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 cursor-pointer font-mono ${
                           on ? "bg-[var(--pop-pink)]/20 text-[var(--pop-pink)] font-bold" : "hover:bg-[var(--pop-cream)]/40"
                         }`}>
                    <input type="checkbox" checked={on} onChange={() => toggle(s.id)} className="scale-90" />
                    {s.code}
                  </label>
                );
              })}
            </div>
          </div>
        )}

        <div className="text-xs text-[var(--text-muted)] mb-3" data-testid="qr-preview-count">
          Bereit zum Druck: <b className="font-mono">{filtered.length}</b> Etiketten · ~<b>{Math.ceil(filtered.length / 2)}</b> Seiten
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Abbrechen</Button>
          <Button onClick={download} disabled={busy || !filtered.length}
                  data-testid="btn-download-qr-labels"
                  className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
            <Download size={14} className="mr-1" /> {busy ? "Erstelle…" : `PDF herunterladen (${filtered.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ZoneDialog({ initial, onClose, onSaved }) {
  const isEdit = initial.mode === "edit";
  const [f, setF] = useState({
    name: initial.name || "",
    color: initial.color || "#eb75b5",
    sort_order: initial.sort_order ?? 0,
    notes: initial.notes || "",
  });
  const save = async () => {
    if (!f.name.trim()) return toast.error("Name Pflicht");
    try {
      const payload = { ...f, sort_order: Number(f.sort_order) };
      if (isEdit) await api.patch(`/shelves/zones/${initial.id}`, payload);
      else await api.post("/shelves/zones", payload);
      toast.success("Gespeichert"); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="dialog-zone">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {isEdit ? `Zone: ${initial.name}` : "Neue Zone"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name *</Label>
            <Input value={f.name} data-testid="input-zone-name"
                   placeholder='z.B. "Halle A", "Kühlhaus"'
                   onChange={(e) => setF({ ...f, name: e.target.value })} />
          </div>
          <div>
            <Label>Farbe (Hex)</Label>
            <div className="flex items-center gap-2">
              <Input type="color" value={f.color}
                     className="w-16 h-10 p-1"
                     data-testid="input-zone-color"
                     onChange={(e) => setF({ ...f, color: e.target.value })} />
              <Input value={f.color}
                     onChange={(e) => setF({ ...f, color: e.target.value })}
                     className="font-mono" />
            </div>
          </div>
          <div>
            <Label>Sortier-Reihenfolge</Label>
            <Input type="number" value={f.sort_order}
                   onChange={(e) => setF({ ...f, sort_order: e.target.value })} />
          </div>
          <div>
            <Label>Notizen</Label>
            <Input value={f.notes}
                   onChange={(e) => setF({ ...f, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Abbrechen</Button>
          <Button onClick={save} data-testid="btn-save-zone"
                  className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
